import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Icon } from '@iconify/react/dist/iconify.js';
import { PHASES, RETENTION_STAGES } from '../constants/stages';
import { RT } from '../theme/retentionTheme';

// 13 nodes in three phase blocks. `currentStage` comes from getWorkflowStageNo; `progress` (optional) is the
// /stages response keyed by stage_no. Clicking a node opens that stage's tab.
export default function StageStepper({ currentStage = 1, progress = {}, onStageClick }) {
  const groups = [1, 2, 3].map((p) => ({ phase: p, stages: RETENTION_STAGES.filter((s) => s.phase === p) }));
  const isDone = (s) => {
    const st = progress[s.no]?.stage_status;
    return st === 2 || (st === undefined && s.no < currentStage);
  };

  return (
    <Box sx={{ overflowX: 'auto', pb: 0.5 }}>
      <Stack direction="row" gap={1.5} sx={{ minWidth: 920 }}>
        {groups.map(({ phase, stages }) => {
          const ph = PHASES[phase];
          const doneCount = stages.filter(isDone).length;
          const active = stages.some((s) => s.no === currentStage);
          return (
            <Box
              key={phase}
              sx={{
                flexGrow: stages.length,
                flexBasis: 0,
                borderRadius: 3,
                p: 1.5,
                pb: 1.25,
                bgcolor: active ? ph.light : '#FAFBFD',
                border: `1px solid ${active ? alpha(ph.color, 0.25) : RT.line}`,
              }}
            >
              <Stack direction="row" alignItems="center" gap={0.75} mb={1.5}>
                <Box component={Icon} icon={ph.icon} width={16} height={16} sx={{ color: ph.color }} />
                <Typography variant="overline" sx={{ color: ph.color, lineHeight: 1 }}>
                  {ph.label}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Typography variant="caption" sx={{ color: ph.color, fontWeight: 800 }}>
                  {doneCount}/{stages.length}
                </Typography>
              </Stack>
              <Stack direction="row">
                {stages.map((s, i) => {
                  const done = isDone(s);
                  const now = s.no === currentStage;
                  return (
                    <Tooltip key={s.no} title={`Stage ${s.no} · ${s.name}`}>
                      <Stack
                        alignItems="center"
                        gap={0.75}
                        onClick={onStageClick ? () => onStageClick(s) : undefined}
                        sx={{
                          flex: 1,
                          position: 'relative',
                          cursor: onStageClick ? 'pointer' : 'default',
                          '&:hover .rt-node': onStageClick ? { transform: 'scale(1.08)' } : {},
                          '&::before':
                            i === 0
                              ? {}
                              : {
                                  content: '""',
                                  position: 'absolute',
                                  top: 17,
                                  left: '-50%',
                                  right: '50%',
                                  height: 3,
                                  borderRadius: 3,
                                  bgcolor: done || now ? ph.color : RT.line,
                                },
                        }}
                      >
                        <Box
                          className="rt-node"
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            display: 'grid',
                            placeItems: 'center',
                            position: 'relative',
                            transition: 'transform .15s',
                            color: done || now ? '#fff' : RT.faint,
                            background: now ? ph.gradient : done ? ph.color : '#fff',
                            border: done || now ? 'none' : `2px solid ${RT.line}`,
                            boxShadow: now ? `0 0 0 5px ${alpha(ph.color, 0.18)}` : 'none',
                          }}
                        >
                          <Icon icon={done ? 'solar:check-read-linear' : s.icon} width={18} height={18} />
                        </Box>
                        <Typography
                          variant="caption"
                          align="center"
                          sx={{
                            lineHeight: 1.2,
                            fontSize: 11.5,
                            fontWeight: now ? 800 : 600,
                            color: now ? ph.color : done ? RT.ink2 : RT.faint,
                          }}
                        >
                          {s.short}
                        </Typography>
                      </Stack>
                    </Tooltip>
                  );
                })}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
